/**
 * Difficulty self-rating modal — gates the study-mode sign flow.
 *
 * Pops up between the rad's "Sign Report" click and the actual sign
 * mutation. The radiologist picks 1–5 (very easy → very hard) and
 * confirms; the signed report mutation only fires after a difficulty
 * is recorded so biostats can control for case complexity.
 *
 * Cancel reverts the click entirely — the report stays unsigned.
 */
import { useEffect, useState } from "react";

export interface StudyDifficultyModalProps {
  onSubmit: (difficulty: number) => void;
  onCancel: () => void;
}

const RATINGS: { value: number; label: string }[] = [
  { value: 1, label: "Very easy" },
  { value: 2, label: "Easy" },
  { value: 3, label: "Moderate" },
  { value: 4, label: "Hard" },
  { value: 5, label: "Very hard" },
];

export const StudyDifficultyModal: React.FC<StudyDifficultyModalProps> = ({
  onSubmit,
  onCancel,
}) => {
  const [selected, setSelected] = useState<number | null>(null);

  // Numeric keyboard shortcuts (1–5 pick + Enter submits / Esc cancels)
  // so the rad can stay on the keyboard after dictation.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === "Enter" && selected !== null) {
        e.preventDefault();
        onSubmit(selected);
        return;
      }
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= 5) {
        e.preventDefault();
        setSelected(n);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onSubmit, onCancel, selected]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-[2px]"
      data-testid="study-difficulty-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Rate case difficulty"
    >
      <div className="bg-bg-2 border border-border-strong rounded shadow-2xl px-8 py-7 max-w-md w-full mx-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-signal-text-tint mb-2">
          Reader-time study
        </p>
        <h2 className="text-xl font-bold text-white mb-1">How difficult was this case?</h2>
        <p className="text-sm text-fg-muted mb-5">
          Your rating is recorded with the signed report. It helps the analysis control for case
          complexity.
        </p>

        <div className="flex items-stretch gap-2 mb-5">
          {RATINGS.map((r) => {
            const active = selected === r.value;
            return (
              <button
                key={r.value}
                type="button"
                onClick={() => setSelected(r.value)}
                className={
                  "flex-1 flex flex-col items-center justify-center gap-1 py-3 rounded-lg border transition-colors " +
                  (active
                    ? "border-signal-stroke bg-signal-tint text-white"
                    : "border-border-strong bg-surface-2/60 text-fg-muted hover:bg-surface-2")
                }
                data-testid={`study-difficulty-option-${r.value}`}
                aria-pressed={active}
              >
                <span className="text-lg font-mono font-bold">{r.value}</span>
                <span className="text-[10px] uppercase tracking-wide">{r.label}</span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-fg-muted hover:text-white"
            data-testid="study-difficulty-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => selected !== null && onSubmit(selected)}
            disabled={selected === null}
            className={
              "px-5 py-2 text-sm font-medium rounded-lg transition-colors " +
              (selected === null
                ? "bg-signal/30 text-signal-text-tint/50 cursor-not-allowed"
                : "bg-signal hover:bg-signal text-white")
            }
            data-testid="study-difficulty-confirm"
          >
            Confirm &amp; sign
          </button>
        </div>
        <p className="text-xs text-fg-muted mt-3 text-center">
          Press{" "}
          <kbd className="font-mono bg-surface-2 border border-border-strong rounded px-1.5">1</kbd>
          –
          <kbd className="font-mono bg-surface-2 border border-border-strong rounded px-1.5">5</kbd>{" "}
          to pick,{" "}
          <kbd className="font-mono bg-surface-2 border border-border-strong rounded px-1.5">
            Enter
          </kbd>{" "}
          to confirm
        </p>
      </div>
    </div>
  );
};
